import {CubaCIRuler, LastNameRuler, NameRuler} from '@dofleini/validator';
import {classifications, partner} from '@mep/pae-commons';
import {Button, Col, Form, Input, message, Row} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import FormBuilder from 'antd-form-builder';
import classNames from 'classnames';
import {debounce} from 'lodash';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useDeepCompareEffect from 'use-deep-compare-effect';

import CivilStatusSelect from '@/components/CivilStatusSelect';
import CountrySelect from '@/components/CountrySelect';
import FloatContainer, {TYPE_CONTAINER} from '@/components/FloatContainer';
import FormTitleSession from '@/components/FormTitleSession';
import GenderSelector from '@/components/GenderSelector';
import InfoBox from '@/components/InfoBox';
import MunicipalitySelect from '@/components/MunicipalitySelect';
import PhoneEditor from '@/components/PhoneNumberField/PhoneEditor';
import ProvinceSelect from '@/components/ProvinceSelect';
import UploadFileDragger from '@/components/UploadFileDragger';
import {useDialogsContext} from '@/contexts/DialogsContext';
import {useResponsive} from '@/contexts/ResponsiveContext';
import FieldSetGroupDivider from '@/modules/authentication/pages/SignUp/components/FieldSet';
import MarriedInformation from '@/modules/authentication/pages/SignUp/components/MarriedInformation';
import SkinColorSelect from '@/modules/proposalRequest/components/SkinColorSelect';
import StudyClassificationSelect from '@/modules/proposalRequest/components/StudyClassificationSelect';
import StudyLevelSelect from '@/modules/proposalRequest/components/StudyLevelSelect';
import {DIALOG_PARTNER_NAMESPACE} from '@/modules/proposalRequest/contants/proposalRequest';
import {useIsValidPartnerIdentifier} from '@/modules/proposalRequest/hooks/useProposalRequest';
import {validations} from '@/utils/fields';

const {CIVIL_STATUS} = partner;
const {STUDY_LEVELS} = classifications;

const ProposalRequestPartnerFormModal = ({
  onConfirm,
  titleText,
  descriptionText,
  isStatePartner,
  proposal,
  isTCPProposal
}) => {
  const {t} = useTranslation(['proposalRequest', 'authentication']);
  const {isOpen, closeDialog, payload} = useDialogsContext(DIALOG_PARTNER_NAMESPACE);
  const {isMobile} = useResponsive();
  const [form] = Form.useForm();
  const forceUpdate = FormBuilder.useForceUpdate();
  const initValues = Object.assign({}, payload?.partner || {});
  const [query, setQuery] = useState(null);
  const identifierDebounce = useRef(query);
  
  const {address, idNumber, civilStatus, civilStatusInfo, studyLevel} = form.getFieldsValue();
  
  const {
    isLoading,
    data
  } = useIsValidPartnerIdentifier(query, proposal?.id, proposal?.subjectType, {enabled: isStatePartner});
  const isIdentifierAvailable = !data?.exist || false;
  
  useEffect(() => {
    isOpen && form.resetFields();
    setTimeout(() => forceUpdate(), 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate, form, isOpen, proposal?.id]);
  
  const onCLose = useCallback(() => {
    closeDialog();
    form.resetFields();
  }, [closeDialog, form]);
  
  const handleSubmit = useCallback(async (values) => {
    if (!isIdentifierAvailable && !isStatePartner && initValues?.idNumber !== values?.idNumber)
      return message.error(t('proposalRequest.form.partners.modal.alreadyUsedIdNumber'));
    await onConfirm(
      {
        ...initValues,
        ...values
      },
      payload?.isEdit,
      payload?.recordIndex
    );
    onCLose();
  }, [isIdentifierAvailable, isStatePartner, t, onConfirm, initValues, payload?.isEdit, payload?.recordIndex, onCLose]);
  
  useDeepCompareEffect(() => {
    if (!isEmpty(initValues)) {
      form.setFieldsValue(initValues);
    }
  }, [initValues]);
  
  const search = useMemo(() => debounce(() => {
    setQuery(identifierDebounce?.current);
  }, 350), []);
  
  useEffect(() => {
    identifierDebounce.current = idNumber;
    search();
  }, [idNumber, search]);
  
  const showClassification = useMemo(() => {
    return studyLevel && [STUDY_LEVELS.TEC_MEDIO_LEVEL, STUDY_LEVELS.TEC_SUPERIOR_LEVEL, STUDY_LEVELS.UNIVERSIDAD_LEVEL].includes(studyLevel);
  }, [studyLevel]);
  
  return (
    <FloatContainer
      type={isMobile ? TYPE_CONTAINER.DRAWER : TYPE_CONTAINER.MODAL}
      visible={isOpen}
      title={null}
      closable
      bodyStyle={{padding: '0.1rem 1.5rem'}}
      onClose={onCLose}
      width={700}
      footer={[
        <Button className="mx-2" key="back" onClick={onCLose}>
          {t('cancel')}
        </Button>,
        <Button className="mx-2" key="submit" type="primary" onClick={() => form.submit()} loading={isLoading}>
          {t('confirm')}
        </Button>
      ]}>
      <Form
        scrollToFirstError
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        onValuesChange={forceUpdate}
        initialValues={initValues}>
        <FormTitleSession
          translate={'proposalRequest'}
          title={titleText || 'proposalRequest.form.partners.modal.title'}
          description={descriptionText || 'authentication:signup.verificationHelp'}/>
        <div>
          <Row gutter={20}>
            <Col span={12} xs={24} md={12}>
              <Form.Item label={t('firstName')} name="firstName" rules={[{required: true}, NameRuler]}>
                <Input size={'large'} placeholder={t('firstNamePlaceHolder')}/>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label={t('middleName')} name="middleName" rules={[NameRuler]}>
                <Input size={'large'} placeholder={t('middleNamePlaceHolder')}/>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label={t('lastName')} name="lastName" rules={[{required: true}, LastNameRuler]}>
                <Input size={'large'} placeholder={t('lastNamePlaceHolder')}/>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label={t('secondName')} name="secondName" rules={[LastNameRuler]}>
                <Input size={'large'} placeholder={t('secondNamePlaceHolder')}/>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={20}>
            <Col span={12} xs={24} md={12}>
              <Form.Item label={t('bornPlace')} name={'bornPlace'} rules={[{required: true}]}
                initialValue={'CU'}>
                <CountrySelect size={'large'}/>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} md={12}>
              <Form.Item label={t('gender')} name={'gender'} rules={[{required: true}]}>
                <GenderSelector size={'large'}/>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            extra={<InfoBox title={t('proposalRequest.form.general.address.helper')}/>}
            required
            label={t('proposalRequest.form.general.address.label')}
            rules={[{required: true}]}
            name={['address', 'abstract']}
          >
            <TextArea maxLength={255} autoSize={{minRows: 2, maxRows: 5}}/>
          </Form.Item>
          <Row gutter={20}>
            <Col span={12} xs={24} lg={12}>
              <Form.Item label={t('Provincia')} rules={[{required: true}]} required name={['address', 'province']}>
                <ProvinceSelect size={'large'} placeholder={t('selectPlaceholder')}/>
              </Form.Item>
            </Col>
            <Col span={12} xs={24} lg={12}>
              <Form.Item required label={t('municipality')} rules={[{required: true}]}
                name={['address', 'municipality']} dependencies={[['address', 'province']]}>
                <MunicipalitySelect province={address?.province} size={'large'} placeholder={t('selectPlaceholder')}/>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="phone"
            label={t('phones')}
            rules={[validations.listPhones(), validations.phoneValidator()]}>
            <PhoneEditor haveType placeholder={t('phonePlaceHolder')} size={'large'}/>
          </Form.Item>
          
          <Form.Item name="email" rules={[{required: true}, {type: 'email'}]} label={t('email')}>
            <Input size={'large'} placeholder={t('emailPlaceHolder')}/>
          </Form.Item>
        </div>
        
        <div>
          <div>
            <Form.Item name={'idNumber'} label={t('dni.placeholder')}
              rules={[{required: true}, CubaCIRuler]}
              required>
              <Input size={'large'} placeholder={t('dniPlaceHolder')} className={classNames('md:w-1/2')}/>
            </Form.Item>
            <Form.Item
              name={'dniPictures'}
              label={t('dni.pictures')}
              rules={[{required: true}, validations?.min2Pictures()]}
              required
              extra={<InfoBox title={t('dni.helpText')}/>}
              initialValue={[]}
            >
              <UploadFileDragger
                accept={'image/jpeg,image/png'}
                aspect={16 / 9}
                cropImage
                multiple
                isImage
                translation={'admin'}
                descriptionText={'upload'}/>
            </Form.Item>
          </div>
          <>
            <FieldSetGroupDivider title={t('fieldSets.extra')}/>
            <div>
              <Form.Item name={'skinColor'} label={t('skinColors.label')} rules={[{required: true}]} required>
                <SkinColorSelect size={'large'}/>
              </Form.Item>
            </div>
            <Row gutter={20}>
              <Col span={showClassification ? 12 : 24} xs={24} md={showClassification ? 12 : 24}>
                <Form.Item name={'studyLevel'} label={t('studyLevels.label')} rules={[{required: true}]} required>
                  <StudyLevelSelect size={'large'}/>
                </Form.Item>
              </Col>
              {showClassification && <Col span={12} xs={24} md={12}>
                <Form.Item name={'studyClassification'} label={t('studyClassifications.label')}
                  rules={[{required: showClassification}]}
                  required={showClassification}>
                  <StudyClassificationSelect size={'large'}/>
                </Form.Item>
              </Col>}
            </Row>
            {!isStatePartner && !isTCPProposal && <div>
              <Form.Item name={'civilStatus'} label={t('civilStatus.placeholder')} rules={[{required: true}]}
                required>
                <CivilStatusSelect size={'large'}/>
              </Form.Item>
              {civilStatus === CIVIL_STATUS.MARRIED &&
                <MarriedInformation value={civilStatusInfo} form={form} mainIdNumber={idNumber}/>}
            </div>}
          </>
        </div>
        
        {isStatePartner &&
          <Form.Item label={t('proposalRequest.form.partners.modal.workPlace')} name="workPlace" required
            rules={[{required: true}]}>
            <Input size={'large'} placeholder={t('defaultPlaceholder')}/>
          </Form.Item>}
      </Form>
    </FloatContainer>);
};

ProposalRequestPartnerFormModal.propTypes = {
  proposal: PropTypes.object,
  titleText: PropTypes.string,
  descriptionText: PropTypes.string,
  isStatePartner: PropTypes.bool,
  isTCPProposal: PropTypes.bool,
  onConfirm: PropTypes.func
};

ProposalRequestPartnerFormModal.defaultProps = {};

export default memo(ProposalRequestPartnerFormModal);
